import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useExploreInitialization } from "./explore/useExploreInitialization";
import { useExploreUrlSync } from "./explore/useExploreUrlSync";
import { usePromptLoading } from "./explore/usePromptLoading";
import { useCategoryFilter } from "./useCategoryFilter";
import { useSearchFilter } from "./useSearchFilter";
import { useEnhancedSearch } from "./useEnhancedSearch";
import { useExploreState } from "./explore/useExploreState";
import { useExploreFilters } from "./explore/useExploreFilters";
import { useExplorePagination } from "./explore/useExplorePagination";
import { useExploreEffects } from "./explore/useExploreEffects";

export function useExplorePrompts() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const categoryParam = queryParams.get('category');
  const searchParam = queryParams.get('q');
  const sortParam = queryParams.get('sort') || 'recent'; // Changed default from 'relevance' to 'recent'
  const timeFilterParam = queryParams.get('time') || '';
  
  console.log("🎯 useExplorePrompts - URL Params:", { categoryParam, searchParam, sortParam, timeFilterParam });

  // State management
  const {
    isInitialized,
    setIsInitialized,
    isCategoryFiltering,
    setIsCategoryFiltering,
    categoriesLoaded,
    setCategoriesLoaded,
    selectedSort,
    setSelectedSort,
    selectedTimeFilter,
    setSelectedTimeFilter,
    isCategoryProcessing,
    initialLoadAttempted,
    categoryChangeTimeout,
    loadDebounceTimer,
    lastDepsString,
    manualLoadTriggered
  } = useExploreState();

  // Initialize categories and handle initial loading
  const {
    categories,
    isLoading: isInitLoading,
    categoryIdFromSlug,
    initialCategoriesLoaded,
    initialLoadComplete
  } = useExploreInitialization({ categoryParam });

  console.log("🏗️ useExplorePrompts - Initialization:", { 
    categoryIdFromSlug, 
    categoriesCount: categories.length,
    initialCategoriesLoaded: initialCategoriesLoaded.current 
  });

  // Initialize sort and time filter from URL params
  useEffect(() => {
    setSelectedSort(sortParam);
    setSelectedTimeFilter(timeFilterParam);
  }, [sortParam, timeFilterParam, setSelectedSort, setSelectedTimeFilter]);

  const {
    selectedCategory,
    handleCategoryFilter,
    clearCategoryFilter,
    setSelectedCategory,
    totalItems
  } = useCategoryFilter({ 
    initialCategoryId: categoryIdFromSlug,
    onCategoryChange: (categoryId) => {
      console.log("📂 Category changed to:", categoryId);
      
      // Set category filtering state to improve UX
      setIsCategoryFiltering(true);
      isCategoryProcessing.current = true;
      
      // Clear previous timeout if exists
      if (categoryChangeTimeout.current) {
        clearTimeout(categoryChangeTimeout.current);
      }
      
      // Set a timeout to clear the loading state
      categoryChangeTimeout.current = setTimeout(() => {
        setIsCategoryFiltering(false);
        isCategoryProcessing.current = false;
      }, 1000);
    }
  });

  console.log("🔗 useExplorePrompts - Category Filter:", { 
    selectedCategory, 
    totalItems, 
    categoryIdFromSlug 
  });

  const {
    searchQuery,
    handleSearch,
    clearSearch
  } = useSearchFilter({ 
    initialQuery: searchParam || "",
    debounceMs: 300
  });

  // Enhanced search hook for when we have a search query
  const {
    results: searchResults,
    isLoading: isSearchLoading,
    resultCount: searchResultCount,
    handleSearchChange: handleEnhancedSearchChange,
    handleFilterChange: handleSearchFilterChange
  } = useEnhancedSearch({
    initialQuery: searchParam || "",
    initialFilters: { 
      categoryFilter: selectedCategory || "",
      sortBy: selectedSort as any,
      timeFilter: selectedTimeFilter
    },
    debounceMs: 300,
    autoSearch: true
  });

  // Pagination logic - pass categoryIdFromSlug to prevent race condition
  const { 
    currentPage, 
    setCurrentPage,
    totalPages,
    ITEMS_PER_PAGE,
    setTotalItems
  } = useExplorePagination({
    totalItems,
    initialCategoriesLoaded,
    selectedCategory,
    searchQuery,
    categoryIdFromSlug // Pass this to prevent loading general count when we have URL category
  });

  console.log("📄 useExplorePrompts - Pagination:", { 
    currentPage, 
    totalPages, 
    totalItems, 
    ITEMS_PER_PAGE 
  });

  // URL synchronization
  useExploreUrlSync({
    selectedCategory,
    searchQuery,
    categories,
    initialCategoriesLoaded,
    isCategoryProcessing
  });

  // Prompt loading for when we don't have a search query
  const {
    prompts: regularPrompts,
    isLoading: isPromptLoading,
    loadPrompts,
    error: promptLoadError
  } = usePromptLoading({
    selectedCategory,
    currentPage,
    searchQuery: "", // Always pass empty string to prevent conflict
    itemsPerPage: ITEMS_PER_PAGE,
    initialLoadComplete,
    sortBy: selectedSort,
    timeFilter: selectedTimeFilter,
    onLoadComplete: () => {
      setIsCategoryFiltering(false);
      if (categoryChangeTimeout.current) {
        clearTimeout(categoryChangeTimeout.current);
        categoryChangeTimeout.current = null;
      }
    }
  });

  // Filter handling
  const { handleSortChange, handleTimeFilterChange, clearFilters } = useExploreFilters({
    setSelectedSort,
    setSelectedTimeFilter,
    setCurrentPage,
    clearCategoryFilter,
    clearSearch,
    loadPrompts
  });

  // Effects and side effects
  useExploreEffects({
    categories,
    initialCategoriesLoaded,
    setCategoriesLoaded,
    categoryIdFromSlug,
    setSelectedCategory,
    searchQuery,
    selectedCategory,
    selectedSort,
    selectedTimeFilter,
    handleEnhancedSearchChange,
    handleSearchFilterChange,
    isCategoryProcessing,
    loadPrompts,
    loadDebounceTimer,
    lastDepsString,
    initialLoadAttempted,
    manualLoadTriggered,
    currentPage,
    isInitialized,
    setIsInitialized,
    categoryChangeTimeout
  });

  // Determine which prompts and loading state to use
  const isSearchActive = !!searchQuery;
  const prompts = isSearchActive ? searchResults : regularPrompts;
  const isLoading = isSearchActive ? isSearchLoading : (isInitLoading && !initialLoadAttempted.current) || isPromptLoading;
  const totalPrompts = isSearchActive ? searchResultCount : (totalItems || 0);

  console.log("📊 useExplorePrompts - Final State:", { 
    isSearchActive, 
    promptsCount: prompts.length, 
    isLoading, 
    totalPrompts,
    totalItemsFromFilter: totalItems,
    searchResultCount
  });

  // Update total items for pagination when in search mode
  useEffect(() => {
    if (isSearchActive && searchResultCount !== undefined) {
      console.log(`🔍 Setting total items to search result count: ${searchResultCount}`);
      setTotalItems(searchResultCount);
    } else if (!isSearchActive && totalItems !== undefined) {
      console.log(`📊 Setting total items to filtered count: ${totalItems}`);
      setTotalItems(totalItems);
    }
  }, [isSearchActive, searchResultCount, totalItems, setTotalItems]);

  // Check if filters are active
  const hasFilters = !!selectedCategory || !!searchQuery || selectedSort !== 'recent' || !!selectedTimeFilter; // Changed from 'relevance' to 'recent'

  return {
    prompts,
    isLoading,
    isInitLoading,
    selectedCategory,
    currentPage,
    setCurrentPage,
    totalPrompts,
    totalPages,
    searchQuery,
    handleSearch,
    handleCategoryFilter,
    clearFilters,
    ITEMS_PER_PAGE,
    categories,
    isCategoryFiltering,
    categoriesLoaded,
    error: promptLoadError,
    selectedSort,
    selectedTimeFilter,
    handleSortChange,
    handleTimeFilterChange,
    hasFilters
  };
}
